import { useId, useRef, useState } from "react";

interface FileButtonProps {
  /** Button text, e.g. "Upload my CV". Becomes "Replace ..." once a file is on file. */
  label: string;
  accept: string;
  onFile: (file: File) => void;
  disabled?: boolean | undefined;
  busy?: boolean | undefined;
  /** Something is already on file, so the action reads as replacing it. */
  hasFile?: boolean | undefined;
  /** Accessible name when the visible label isn't specific enough (several pickers on one screen). */
  ariaLabel?: string | undefined;
}

/**
 * A file picker that looks like the button it is.
 *
 * The browser's native `<input type="file">` renders as a small grey control plus "No file chosen" text, which
 * participants read as a label rather than something to tap. The real input stays in the page (visually hidden, still
 * focusable and announced by screen readers) and a styled label opens it, so keyboard, touch and the camera/files
 * sheet on phones all behave exactly as before.
 *
 * Also accepts a dropped file on desktop.
 */
export function FileButton({
  label,
  accept,
  onFile,
  disabled,
  busy,
  hasFile,
  ariaLabel,
}: FileButtonProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const off = disabled || busy;

  const take = (file: File | undefined) => {
    if (file && !off) onFile(file);
    // Reset so choosing the same file again (after fixing it) still fires a change.
    if (input.current) input.current.value = "";
  };

  return (
    <div>
      <input
        ref={input}
        id={id}
        type="file"
        accept={accept}
        disabled={off}
        aria-label={ariaLabel ?? label}
        className="peer sr-only"
        onChange={(event) => take(event.target.files?.[0])}
      />
      <label
        htmlFor={id}
        onDragOver={(event) => {
          event.preventDefault();
          if (!off) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          take(event.dataTransfer.files[0]);
        }}
        className={`inline-flex min-h-11 select-none items-center justify-center gap-2 rounded-[var(--radius-control)] border-[1.5px] px-[22px] text-[14.5px] font-bold transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent ${
          off
            ? "cursor-not-allowed border-border bg-surface-high text-text-muted opacity-60"
            : dragging
              ? "cursor-copy border-accent bg-accent-tint text-accent"
              : "cursor-pointer border-accent bg-surface text-accent hover:bg-accent-tint"
        }`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 16V4m0 0l-5 5m5-5l5 5M5 20h14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {busy ? "Uploading..." : hasFile ? label.replace(/^(Upload|Choose)/, "Replace") : label}
      </label>
    </div>
  );
}
